/**
 * Lesson content for the Learn mode.
 *
 * Each topic has a short, authored mini-lesson designed to be read in a few
 * minutes before practicing: a concept intro (what it means and why the method
 * works), a "key idea" recognition cue, an ordered method, common pitfalls,
 * vocabulary, and one conceptual quick check. This is the "teach me first"
 * companion to Practice's "let me try". Worked examples are NOT authored here -
 * the Learn screen pulls a live, engine-verified example from the topic's
 * generator, so the numbers in the example are always correct by construction.
 *
 * Content is standard first-nine-weeks Algebra I. Text fields are plain
 * strings; short inline math may use KaTeX delimited by $...$ which the Learn
 * screen renders inline.
 */

import type { TopicId } from "./curriculum";

/** A single conceptual multiple-choice check shown at the end of a lesson. */
export interface QuickCheck {
  /** The question. Targets the lesson's central concept or misconception. */
  readonly prompt: string;
  /** 3-4 answer choices. */
  readonly choices: readonly string[];
  /** Index into `choices` of the correct answer. */
  readonly correctIndex: number;
  /** Shown after answering: why the correct answer is correct. */
  readonly explanation: string;
}

export interface Lesson {
  readonly topicId: TopicId;
  /** A paragraph: what this topic is and why the method works. */
  readonly intro: string;
  /**
   * A short recognition cue: how to spot this kind of problem and the single
   * most important idea to hold onto. Rendered as a highlighted callout.
   */
  readonly keyIdea?: string;
  /** The ordered method a student follows to solve these problems. */
  readonly methodSteps: readonly string[];
  /** Common mistakes to watch for. */
  readonly pitfalls: readonly string[];
  /** Optional key terms worth knowing. */
  readonly vocabulary?: readonly { readonly term: string; readonly definition: string }[];
  /**
   * Conceptual check(s) shown before the Practice button. `quickCheck` is the
   * single-question form (kept for compatibility); `quickChecks` is the
   * multi-question form. The Learn screen renders `quickChecks` when present,
   * otherwise the single `quickCheck`.
   */
  readonly quickCheck?: QuickCheck;
  readonly quickChecks?: readonly QuickCheck[];
}

/**
 * Resolve a lesson's quick checks to a single list, preferring the multi form.
 */
export function lessonQuickChecks(lesson: Lesson): readonly QuickCheck[] {
  if (lesson.quickChecks && lesson.quickChecks.length > 0) {
    return lesson.quickChecks;
  }
  return lesson.quickCheck ? [lesson.quickCheck] : [];
}

export const LESSONS: Record<TopicId, Lesson> = {
  // --- Unit 1: Solving Linear Equations ---------------------------------
  "two-step-fractions": {
    topicId: "two-step-fractions",
    intro:
      "Solving an equation means getting the variable by itself. You do that with inverse operations - operations that undo each other, like adding undoes subtracting and dividing undoes multiplying. A two-step equation takes two of these undo moves. Here one of them involves a fractional coefficient. To undo it you can divide by that fraction, which is perfectly valid; in practice multiplying by its reciprocal (the fraction flipped over) is the same operation and is usually cleaner to work with. It works because a nonzero fraction times its reciprocal equals 1, and multiplying the variable by 1 leaves it alone - exactly what 'isolate the variable' means.",
    methodSteps: [
      "Look at what is being done to the variable. Usually it is multiplied by a fraction, then a number is added or subtracted.",
      "Undo the adding or subtracting first, because it is the outer, last-applied operation. Add or subtract the same amount on both sides so the equation stays balanced.",
      "Now undo the fraction. You can divide both sides by it, but multiplying by its reciprocal is the same move and is usually easier. For example, to undo $\\tfrac{2}{3}x$ multiply by $\\tfrac{3}{2}$, because $\\tfrac{3}{2}\\cdot\\tfrac{2}{3}=1$, leaving $1x$.",
      "Reduce your answer to lowest terms, then check it by putting it back into the original equation.",
    ],
    keyIdea:
      "Recognize it when a variable is multiplied by a fraction and then a number is added or subtracted. Undo the add/subtract first, then undo the fraction - dividing by it works, but multiplying by the reciprocal is the cleaner equivalent.",
    pitfalls: [
      "Multiplying by the fraction itself, which does not undo it. To undo $\\tfrac{2}{3}x$, divide by $\\tfrac{2}{3}$ or (the same thing) multiply by $\\tfrac{3}{2}$ - not by $\\tfrac{2}{3}$.",
      "Changing only one side. Whatever you do has to happen on both sides to keep the equation balanced.",
      "Leaving the answer unreduced, like writing $\\tfrac{6}{9}$ instead of $\\tfrac{2}{3}$.",
    ],
    vocabulary: [
      { term: "Reciprocal", definition: "A fraction flipped over. The reciprocal of $\\tfrac{a}{b}$ is $\\tfrac{b}{a}$, and a number times its reciprocal is 1." },
      { term: "Inverse operation", definition: "The operation that undoes another: addition/subtraction undo each other, and multiplication/division undo each other." },
    ],
    quickCheck: {
      prompt: "To undo multiplication by $\\tfrac{2}{3}$, what is the cleaner move (equivalent to dividing by $\\tfrac{2}{3}$)?",
      choices: ["Multiply by $\\tfrac{2}{3}$", "Multiply by $\\tfrac{3}{2}$", "Multiply by $3$", "Multiply by $-\\tfrac{2}{3}$"],
      correctIndex: 1,
      explanation:
        "Dividing by $\\tfrac{2}{3}$ is the same as multiplying by its reciprocal $\\tfrac{3}{2}$, because $\\tfrac{3}{2}\\cdot\\tfrac{2}{3}=1$, which leaves the variable isolated.",
    },
  },
  "multi-step-equations": {
    topicId: "multi-step-equations",
    intro:
      "Some equations are not ready to solve yet - a side is cluttered with parentheses or several like terms. There are really two different kinds of moves here, and keeping them straight is the whole idea. First you simplify each side: distributing and combining like terms just rewrites one side as an equal expression, and you do not touch the other side. Then you solve: you apply the same inverse operation to both sides to isolate the variable, which keeps the equation balanced. Doing the simplifying first is convenient because it leaves a clean two-step equation; a handy way to think about the undo order is to peel operations off in the reverse of order of operations, but the real reason the whole process works is that equal expressions and balanced operations both preserve the solution.",
    keyIdea:
      "Two kinds of moves: simplifying a side (rewrite it as an equal expression, other side untouched) is different from operating on both sides (keeps the equation balanced). Simplify each side first, then isolate the variable.",
    methodSteps: [
      "Step 1: Use the distributive property to remove any grouping symbols. Distributing multiplies the outside number by every term inside, so $3(x+2)=3x+6$. This only rewrites that side.",
      "Step 2: Simplify each side by combining like terms. Like terms share the same variable part, so $2x+5x=7x$, but $7x$ and $4$ cannot combine.",
      "Step 3: Collect the variable term on one side and the constant terms on the other, using inverse operations on both sides.",
      "Step 4: Isolate the variable by dividing both sides by its coefficient (the number multiplying it).",
      "Step 5: Check your solution by substituting it into the original equation.",
    ],
    pitfalls: [
      "Distributing to only the first term inside the parentheses. It must reach every term.",
      "Dropping a negative when distributing a negative: $-3(x-2)=-3x+6$, not $-3x-6$.",
      "Trying to combine terms that are not alike - a plain number never combines with an $x$ term.",
      "Confusing the two kinds of moves: combining like terms happens on one side only, but adding or dividing to isolate the variable must be done to both sides.",
    ],
    vocabulary: [
      { term: "Coefficient", definition: "The number multiplied by a variable. In $7x$ the coefficient is 7." },
      { term: "Like terms", definition: "Terms with the exact same variable part, so they can be added or subtracted together." },
      { term: "Two valid methods", definition: "For a problem like $2(1-x)+3=-8$ you can distribute first, or treat the group $(1-x)$ as a single quantity and undo the outside operations first. Both give the same solution." },
    ],
    quickCheck: {
      prompt:
        "You rewrite $3(x+2)$ as $3x+6$ on the left side of an equation. Do you have to change the right side too?",
      choices: [
        "Yes - whatever you do to one side you do to the other",
        "No - this only rewrites the left side as an equal expression",
        "Only if the right side also has parentheses",
        "Yes, but only when distributing a negative",
      ],
      correctIndex: 1,
      explanation:
        "Distributing is simplifying: $3(x+2)$ and $3x+6$ are the same value, so rewriting one side changes nothing about the equation and the other side stays put. The 'do it to both sides' rule applies to operating on the equation (like adding or dividing to isolate the variable), not to simplifying a single side.",
    },
  },
  "variables-both-sides": {
    topicId: "variables-both-sides",
    intro:
      "When the variable shows up on both sides of the equals sign, you cannot isolate it until it lives on only one side. So the first job is to gather all the variable terms onto one side and all the plain numbers onto the other, then finish like a normal equation. This works because adding or subtracting the same term on both sides keeps the equation balanced - both sides change by the same amount, so the solution never changes. You can collect the variable terms on either side; both approaches are mathematically valid and give the same answer. When you have a choice, collecting onto the side that leaves a positive coefficient often makes the arithmetic easier and reduces sign mistakes. Watch for two special cases: if the variable cancels and leaves a false statement, there is no solution; if it leaves a true statement, the equation is an identity (true for every value, so infinitely many solutions).",
    keyIdea:
      "Recognize it when the same variable appears on both sides of the equals sign. The key move is to get all variable terms onto one side (either side is fine) by adding or subtracting the same term on both sides.",
    methodSteps: [
      "Distribute and combine like terms on each side so each side is as simple as possible.",
      "Collect the variable terms on one side by adding or subtracting a variable term on both sides. Either side works; picking the one that keeps the coefficient positive is just easier.",
      "Collect the plain numbers on the other side the same way.",
      "Divide both sides by the coefficient of the variable to isolate it.",
      "Check the answer. If the variable cancels out entirely, read what is left: a false statement means no solution, a true statement means the equation is an identity with infinitely many solutions.",
    ],
    pitfalls: [
      "Moving the constant but forgetting there is still a variable term on the other side to deal with.",
      "Sign errors when subtracting a variable term from both sides - subtract it from every term on that side, not just one.",
      "Misreading the special cases. If you reach $5 = 8$, that statement is false no matter what the variable is, so no value works: no solution. If you reach $6 = 6$, that statement is always true, so the equation is an identity: every value satisfies it (infinitely many solutions).",
    ],
    vocabulary: [
      { term: "No solution", definition: "The variable cancels and leaves a false statement like $5=8$, so no value can make the equation true." },
      { term: "Identity", definition: "An equation that is true for all values of the variable. When solving, the variable cancels and leaves a true statement like $6=6$, meaning infinitely many solutions." },
    ],
    quickChecks: [
      {
        prompt:
          "While solving, the variable cancels out and you are left with $4 = 9$. What does this tell you?",
        choices: ["$x = 4$", "$x = 9$", "No solution", "Infinitely many solutions"],
        correctIndex: 2,
        explanation:
          "The variable is gone, so the result no longer depends on it. $4 = 9$ is false for every value, so nothing can make the equation true: no solution. (A true leftover like $6=6$ would instead make it an identity - infinitely many solutions.)",
      },
      {
        prompt:
          "You have $8x + 3 = 5x + 12$. Which move best starts the solution?",
        choices: [
          "Subtract $5x$ from both sides to collect the variable on the left",
          "Subtract $3$ from the left side only",
          "Divide both sides by $8$ right away",
          "Add $5x$ to both sides",
        ],
        correctIndex: 0,
        explanation:
          "Get the variable onto one side first. Subtracting $5x$ from both sides leaves $3x + 3 = 12$, which is a normal two-step equation. (Collecting on the right by subtracting $8x$ is equally valid; this way just keeps the coefficient positive.)",
      },
    ],
  },
  proportions: {
    topicId: "proportions",
    intro:
      "A proportion says two ratios are equal, like $\\tfrac{a}{b}=\\tfrac{c}{d}$. A ratio is just a comparison of two amounts written as a fraction. To solve, you cross-multiply, which turns the proportion into a plain equation with no fractions. Cross-multiplying is legal because it is really multiplying both sides by both denominators at once - the fractions clear and an equal statement remains.",
    methodSteps: [
      "Write the proportion in the form $\\tfrac{a}{b} = \\tfrac{c}{d}$.",
      "Cross-multiply: multiply each numerator by the other fraction's denominator and set the products equal, so $a\\cdot d = b\\cdot c$.",
      "Solve the equation that results. Distribute first if a numerator or denominator is an expression like $(x+3)$.",
      "Check by plugging your answer back in and confirming the two ratios really are equal.",
    ],
    keyIdea:
      "Recognize it when two ratios (fractions) are set equal. Cross-multiplying clears both denominators at once, turning the proportion into an ordinary equation.",
    pitfalls: [
      "Multiplying the wrong pairs. Each numerator pairs with the opposite fraction's denominator.",
      "Forgetting to distribute when a top or bottom is an expression, such as $2(x+3)$.",
      "Accepting an answer that makes a denominator zero - that value is not allowed, since you cannot divide by zero.",
    ],
    vocabulary: [
      { term: "Ratio", definition: "A comparison of two quantities, written as a fraction." },
      { term: "Proportion", definition: "A statement that two ratios are equal, like $\\tfrac{a}{b}=\\tfrac{c}{d}$." },
      { term: "Cross-multiply", definition: "Multiply each numerator by the other fraction's denominator and set the two products equal." },
    ],
    quickCheck: {
      prompt: "Why is cross-multiplying $\\tfrac{a}{b}=\\tfrac{c}{d}$ into $ad = bc$ a legal step?",
      choices: [
        "It is a shortcut with no real reason",
        "It multiplies both sides by $bd$, clearing the denominators",
        "It flips both fractions upside down",
        "It only works when $b = d$",
      ],
      correctIndex: 1,
      explanation:
        "Multiplying both sides of the equality by $bd$ cancels the denominators: the left becomes $ad$ and the right becomes $bc$. Cross-multiplying is just a fast way to write that denominator-clearing step.",
    },
  },
  "absolute-value-equations": {
    topicId: "absolute-value-equations",
    intro:
      "Absolute value is a number's distance from zero on the number line, so it is never negative - the bars in $|x|$ strip away the sign. That is why $|x|=5$ has two answers: both 5 and -5 sit exactly 5 units from zero. Solving an absolute value equation means asking 'what could have been inside those bars?' - and there are usually two possibilities.",
    methodSteps: [
      "Get the absolute value expression by itself on one side first.",
      "Check the number it equals. If it is negative, stop - there is no solution, since distance can never be negative.",
      "Otherwise split into two equations: the inside equals the positive value, and the inside equals the negative value.",
      "Solve each equation on its own.",
      "Check both answers in the original equation.",
      "Two absolute values: for $|ax+b| = |cx+d|$, the insides are equal or opposite. Write $ax+b = cx+d$ or $ax+b = -(cx+d)$, then solve each.",
    ],
    keyIdea:
      "Recognize it by the absolute-value bars, like $|x|$. Because absolute value is distance from zero, ask 'what could have been inside the bars?' - usually two possibilities, one positive and one negative.",
    pitfalls: [
      "Reporting only one answer and forgetting the negative case.",
      "Splitting into two cases before the absolute value is isolated.",
      "Missing that $|\\text{expression}| = \\text{a negative number}$ has no solution.",
      "For $|ax+b| = |cx+d|$, forgetting to negate the whole right side: the second case is $ax+b = -(cx+d)$, so distribute the negative to every term.",
    ],
    vocabulary: [
      { term: "Absolute value", definition: "A number's distance from zero on the number line; always zero or positive." },
    ],
    quickCheck: {
      prompt: "How many solutions does $|x| = 5$ have, and why?",
      choices: [
        "One, because $x = 5$",
        "Two, because both $5$ and $-5$ are $5$ units from zero",
        "None, because absolute value is always positive",
        "Infinitely many",
      ],
      correctIndex: 1,
      explanation:
        "Absolute value measures distance from zero, and two numbers sit 5 units away: $5$ and $-5$. That is why these equations usually split into two cases.",
    },
  },
  "literal-equations": {
    topicId: "literal-equations",
    intro:
      "A literal equation is a formula with several letters, like $d=rt$, and 'solving' it means rearranging it to get one chosen letter by itself. The moves are identical to solving for $x$ - use inverse operations - you just carry letters instead of numbers. Because the other letters stand for fixed (but unknown) numbers, you treat them exactly like numbers while you rearrange. The answer is an expression, not a single value.",
    methodSteps: [
      "Decide which variable you are solving for. That is the one you want alone at the end.",
      "Treat every other letter as if it were an ordinary number.",
      "Peel off operations with inverse operations, one at a time, doing the same thing to both sides.",
      "Stop when your chosen variable is alone. The answer will be written in terms of the other letters.",
    ],
    keyIdea:
      "Recognize it when a formula has several letters and you are asked to solve for one of them. Treat the other letters as ordinary numbers and use the same balancing moves as any equation.",
    pitfalls: [
      "Expecting a single number - the answer here is an expression that still contains letters.",
      "Dividing only part of a side by a coefficient. The whole side gets divided, so $\\tfrac{y-b}{m}$, not $\\tfrac{y}{m}-b$.",
      "Assuming there is only one right-looking answer. Equivalent rearrangements can look different and still be correct.",
    ],
    vocabulary: [
      { term: "Literal equation", definition: "An equation with more than one variable, usually a formula, solved for one of them." },
      { term: "Inverse operation", definition: "The operation that undoes another; used to peel operations off the target variable." },
    ],
    quickCheck: {
      prompt: "When you solve the formula $d = rt$ for $t$, what should the answer look like?",
      choices: [
        "A single number",
        "An expression in terms of the other letters, $t = \\tfrac{d}{r}$",
        "Another absolute value",
        "$t = dr$",
      ],
      correctIndex: 1,
      explanation:
        "The other letters stand for fixed but unknown quantities, so the result is an expression, not a number. Dividing both sides by $r$ isolates $t$, giving $t = \\tfrac{d}{r}$.",
    },
  },
  "translating-equations": {
    topicId: "translating-equations",
    intro:
      "Word problems become solvable once you translate the sentence into math. First tell apart an expression from an equation: an expression like $x - 5$ is a phrase with no equals sign, while an equation like $x - 5 = 12$ makes a full statement. The word 'is' (or 'equals') is what creates the equals sign. Certain words reliably signal operations: 'sum' means add, 'difference' means subtract, 'product' means multiply, 'quotient' means divide. The part that trips people up is order - subtraction and division phrases sometimes build backward from how you read them.",
    keyIdea:
      "'Is' or 'equals' marks the equals sign and turns an expression into an equation. Translate each phrase to an operation, and read subtraction and division phrases carefully - their order can flip.",
    methodSteps: [
      "Find the unknown quantity and pick a letter to stand for it.",
      "Break the sentence into short phrases and translate each phrase into math.",
      "Watch order-sensitive phrases. '5 less than $x$' is $x - 5$; 'the quotient of $x$ and 4' is $\\tfrac{x}{4}$, but '4 divided by $x$' is $\\tfrac{4}{x}$.",
      "Join the phrases into one equation, using 'is' or 'equals' as the equals sign. No 'is'? Then you only have an expression, not an equation.",
    ],
    pitfalls: [
      "Reversing subtraction phrases: '5 less than $x$' is $x - 5$, but 'x less than 5' is $5 - x$.",
      "Reversing division phrases: 'the quotient of $x$ and 4' is $\\tfrac{x}{4}$, while '4 divided by $x$' is $\\tfrac{4}{x}$.",
      "Missing shorthand: 'twice a number' is $2x$, and 'a number squared' is $x^2$.",
    ],
    vocabulary: [
      { term: "Expression", definition: "A math phrase with no equals sign, like $x-5$." },
      { term: "Equation", definition: "A statement that two expressions are equal, like $x-5=12$; the word 'is' creates it." },
      { term: "Sum / difference", definition: "The result of adding / subtracting." },
      { term: "Product / quotient", definition: "The result of multiplying / dividing." },
    ],
    quickCheck: {
      prompt: "Which expression means '5 less than a number $x$'?",
      choices: ["$5 - x$", "$x - 5$", "$5x$", "$x + 5$"],
      correctIndex: 1,
      explanation:
        "'Less than' reverses the order you read it: you start with $x$ and take 5 away, giving $x - 5$. ($5 - x$ would be 'x less than 5'.)",
    },
  },

  // --- Unit 2: Solving Linear Inequalities ------------------------------
  "writing-graphing-inequalities": {
    topicId: "writing-graphing-inequalities",
    intro:
      "An equation has one answer; an inequality describes a whole range of answers. Instead of 'x equals 4' it says something like 'x is at least 4', meaning 4 and everything above it. You show that range on a number line by marking the boundary (the number the range starts at) and shading every value that works.",
    keyIdea:
      "Recognize it when a problem describes a range rather than one exact value. The boundary is where the range starts; the symbol decides whether that boundary is included (closed circle) or not (open circle).",
    methodSteps: [
      "Turn the phrase into a symbol: 'at least' and 'no fewer than' are $\\ge$; 'at most' and 'no more than' are $\\le$; 'more than' is $>$; 'fewer/less than' is $<$.",
      "Mark the boundary number on the number line.",
      "Choose the circle: open for $<$ or $>$ (the boundary is not included) and closed/filled for $\\le$ or $\\ge$ (the boundary is included).",
      "Shade the side that makes the statement true. If unsure, test a point - if it makes the inequality true, shade toward it.",
    ],
    pitfalls: [
      "Mixing up open and closed circles. 'Or equal to' (which includes 'at least', 'at most', 'no more than', 'no fewer than') always means a closed, filled circle.",
      "Shading the wrong direction. Testing one point removes the guesswork.",
    ],
    vocabulary: [
      { term: "Boundary", definition: "The number the inequality compares to; the point where the shading starts." },
      { term: "Open vs. closed circle", definition: "Open means the boundary is not included ($<,>$); closed/filled means it is included ($\\le,\\ge$)." },
    ],
    quickCheck: {
      prompt: "You graph $x \\ge 4$. What kind of circle goes on 4, and which way do you shade?",
      choices: [
        "Open circle, shade left",
        "Closed circle, shade right",
        "Open circle, shade right",
        "Closed circle, shade left",
      ],
      correctIndex: 1,
      explanation:
        "$\\ge$ includes 4, so the circle is closed (filled). 'Greater than or equal to' means values at 4 and above, so shade toward the larger numbers, to the right.",
    },
  },
  "multi-step-inequalities": {
    topicId: "multi-step-inequalities",
    intro:
      "You solve an inequality almost exactly like an equation - distribute, combine, undo operations - with one rule that has no equivalent for equations: if you multiply or divide both sides by a negative number, you must flip the inequality symbol. Why? Multiplying by a negative reverses the order of the number line (for example $3 > 2$, but $-3 < -2$), so the symbol has to reverse to stay true.",
    methodSteps: [
      "Distribute and combine like terms to simplify each side.",
      "Use inverse operations to get the variable term by itself.",
      "Divide by the coefficient. If that coefficient is negative, reverse the inequality symbol as you divide.",
      "Write the solution, and graph it on a number line if the problem asks.",
    ],
    keyIdea:
      "Solve like an equation, but stay alert for one thing: multiplying or dividing both sides by a negative reverses the inequality symbol, because a negative flips the order of the number line.",
    pitfalls: [
      "Forgetting to flip the symbol when you multiply or divide by a negative.",
      "Flipping the symbol when you only added or subtracted - those never flip it. Only multiplying or dividing by a negative does.",
    ],
    vocabulary: [
      { term: "Reverse the inequality", definition: "Change $<$ to $>$ (and $\\le$ to $\\ge$) whenever you multiply or divide both sides by a negative number." },
    ],
    quickChecks: [
      {
        prompt: "When do you reverse the inequality symbol?",
        choices: [
          "When adding a negative number",
          "When subtracting a number",
          "When multiplying or dividing both sides by a negative",
          "Every time the variable switches sides",
        ],
        correctIndex: 2,
        explanation:
          "Only multiplying or dividing both sides by a negative reverses the symbol, because a negative reverses the order of the number line (for example $3 > 2$ but $-3 < -2$). Adding or subtracting never flips it.",
      },
      {
        prompt: "Solving $-2x < 6$, you divide both sides by $-2$. What is the result?",
        choices: ["$x < -3$", "$x > -3$", "$x < 3$", "$x > 3$"],
        correctIndex: 1,
        explanation:
          "Dividing by $-2$ gives $-3$, and because you divided by a negative you flip the symbol: $x > -3$. You can check with $x = 0$: $-2(0) = 0 < 6$ is true, and $0 > -3$, so the direction is right.",
      },
    ],
  },
  "compound-inequalities": {
    topicId: "compound-inequalities",
    intro:
      "A compound inequality is two inequalities joined by 'and' or 'or'. 'And' means a value has to satisfy both at once, so the solution is the overlap - a band between two boundaries. 'Or' means satisfying at least one is enough, so the solution is everything either one covers - two pieces heading opposite directions. Reading 'and' vs. 'or' correctly is the whole game, because the graphs look completely different.",
    methodSteps: [
      "For an 'and' inequality written as $a < x < b$, work on all three parts at once, doing the same operation to each.",
      "For an 'or' inequality, solve the two inequalities separately.",
      "Graph 'and' as the overlap: a segment between the two boundaries. Graph 'or' as two rays pointing outward.",
      "If you divide any part by a negative, flip that symbol.",
    ],
    keyIdea:
      "Recognize it by the word joining two inequalities. 'And' means the intersection (values in both) - an overlap. 'Or' means the union (values in either) - two regions.",
    pitfalls: [
      "Treating 'and' and 'or' as the same - they give different solutions and different graphs.",
      "In a triple inequality, applying a step to only two of the three parts.",
    ],
    vocabulary: [
      { term: "AND (intersection)", definition: "Values that satisfy both inequalities at once; the overlap of the two solution sets." },
      { term: "OR (union)", definition: "Values that satisfy at least one inequality; everything covered by either solution set." },
    ],
    quickCheck: {
      prompt: "An 'and' compound inequality asks for the values that are...",
      choices: [
        "in either inequality (the union)",
        "in both inequalities at once (the intersection)",
        "in neither inequality",
        "greater than both boundaries",
      ],
      correctIndex: 1,
      explanation:
        "'And' requires both conditions to hold, so the solution is the intersection - the overlap where the two solution sets meet. ('Or' would be the union, everything either one covers.)",
    },
  },
  "absolute-value-inequalities": {
    topicId: "absolute-value-inequalities",
    intro:
      "Because absolute value means distance from zero, an absolute value inequality is really a statement about distance, and it turns into a compound inequality. 'Less than' asks for values close to zero - within a band - so it becomes an 'and'. 'Greater than' asks for values far from zero - out past both ends - so it becomes an 'or'. A memory hook: 'less thAND' and 'greatOR'.",
    methodSteps: [
      "Isolate the absolute value expression first.",
      "For $|\\text{expr}| < k$, write the 'and' inequality $-k < \\text{expr} < k$ (a band in the middle).",
      "For $|\\text{expr}| > k$, write the 'or' inequality $\\text{expr} < -k$ or $\\text{expr} > k$ (two ends).",
      "Solve for the variable and graph the result.",
    ],
    keyIdea:
      "Read it as a distance statement. $|x| < 5$ means 'within 5 units of zero', which is a band ($-5 < x < 5$, an 'and'). $|x| > 5$ means 'more than 5 units away', which is two ends ($x < -5$ or $x > 5$, an 'or').",
    pitfalls: [
      "Mixing up the two cases. Reason from distance first; the hooks 'less thAND' (a band) and 'greatOR' (two ends) are just reminders.",
      "Special cases (these come from absolute value always being zero or positive): $|\\text{expr}| > -3$ is true for every value, while $|\\text{expr}| < -3$ has no solution. At the boundary, $|\\text{expr}| \\ge 0$ is always true and $|\\text{expr}| \\le 0$ holds only when the inside is 0. The problems here use a positive threshold, so you will usually get a normal band or two-ends answer.",
    ],
    vocabulary: [
      { term: "Absolute value", definition: "Distance from zero; here it turns an inequality into a compound one." },
    ],
    quickCheck: {
      prompt: "Thinking about distance from zero, $|x| < 5$ is the same as which inequality?",
      choices: ["$x < -5$ or $x > 5$", "$-5 < x < 5$", "$x > 5$", "$x < 5$ only"],
      correctIndex: 1,
      explanation:
        "$|x| < 5$ says $x$ is within 5 units of zero, so it lies between $-5$ and $5$: the band $-5 < x < 5$ (an 'and'). The 'or' form would come from a 'greater than'.",
    },
  },

  // --- Unit 3: Functions & Relations ------------------------------------
  "discrete-continuous": {
    topicId: "discrete-continuous",
    intro:
      "Data can be discrete or continuous, and the difference is whether in-between values make sense. Discrete data comes in separate, countable chunks - you can have 3 students but not 3.5. Continuous data can be any value in a range, including fractions and decimals - time, distance, and temperature can all land anywhere. The context decides which one you have, not just the numbers.",
    methodSteps: [
      "Ask: can this quantity take an in-between value, like a fraction or decimal, and still make sense?",
      "If only whole, separate values are meaningful, it is discrete.",
      "If any value across a range is possible, it is continuous.",
      "On a graph, discrete data is a set of separate points; continuous data is an unbroken line or curve.",
    ],
    keyIdea:
      "Ask whether an in-between value would make sense. If only separate, countable values are meaningful it is discrete; if any value in a range is possible it is continuous. Context decides.",
    pitfalls: [
      "Assuming anything numerical is continuous. Counts of things (people, cars, tickets) are discrete.",
      "Ignoring context - the same measurement can be discrete or continuous depending on what it counts.",
    ],
    vocabulary: [
      { term: "Discrete", definition: "Takes only separate, countable values, with no meaningful in-between." },
      { term: "Continuous", definition: "Takes any value within a range, including fractions and decimals." },
    ],
    quickCheck: {
      prompt: "The number of students in a classroom is which kind of data?",
      choices: [
        "Continuous, because it is a number",
        "Discrete, because you count whole students and 3.5 makes no sense",
        "Continuous, because it can grow over time",
        "Neither",
      ],
      correctIndex: 1,
      explanation:
        "You count students in whole units - there is no meaningful 3.5 students - so the values are separate and countable: discrete. Something like height or time, where in-between values make sense, would be continuous.",
    },
  },
  "domain-range": {
    topicId: "domain-range",
    intro:
      "Every relation pairs inputs with outputs. The domain is the set of all the inputs (the x-values), and the range is the set of all the outputs (the y-values). You can read them off a list of points, a table, or a graph. A set lists each distinct value once, so there are no repeats. The order does not change the set - $\\{1,3,5\\}$ and $\\{5,1,3\\}$ are the same set - but writing the values from least to greatest just makes your answer easier to read.",
    methodSteps: [
      "For the domain, collect every x-value (the first number in each ordered pair).",
      "For the range, collect every y-value (the second number in each pair).",
      "Write each distinct value once. Unless the problem asks for another format, list them from least to greatest for readability.",
      "From a graph, scan left-to-right to see the domain and bottom-to-top to see the range.",
    ],
    keyIdea:
      "Domain = inputs = x-values; range = outputs = y-values. These are just two names for the same idea: what goes in, and what comes out.",
    pitfalls: [
      "Swapping the two. Domain is always the inputs (x); range is always the outputs (y).",
      "Listing a value more than once - a set records each value a single time.",
    ],
    vocabulary: [
      { term: "Domain", definition: "The set of all input values, the x-values." },
      { term: "Range", definition: "The set of all output values, the y-values." },
    ],
    quickCheck: {
      prompt: "For the points $(1, 4)$, $(2, 7)$, and $(5, 7)$, what is the domain?",
      choices: [
        "$\\{4, 7\\}$",
        "$\\{1, 2, 5\\}$",
        "$\\{1, 2, 5, 4, 7\\}$",
        "$\\{4, 7, 7\\}$",
      ],
      correctIndex: 1,
      explanation:
        "The domain is the set of inputs - the x-values (first coordinates): $\\{1, 2, 5\\}$. The y-values $\\{4, 7\\}$ would be the range, and repeats are listed only once.",
    },
  },
  "function-rules": {
    topicId: "function-rules",
    intro:
      "A function rule is a formula that turns each input into exactly one output, like $y = 2x + 1$. Applying the rule just means substituting - putting the input in place of $x$ - and then simplifying. Following the order of operations matters here: you multiply before you add, or you will get the wrong output.",
    methodSteps: [
      "Substitute the given input in place of every $x$ in the rule.",
      "Simplify using order of operations - multiply first, then add or subtract.",
      "The number you get is the output for that input.",
      "To go backward (find the input for a known output), set the rule equal to that output and solve for $x$.",
    ],
    keyIdea:
      "A function is a rule giving each input exactly one output. Given an input, substitute to evaluate (get the output). Given an output, set the rule equal to it and solve to find the input - opposite directions.",
    pitfalls: [
      "Skipping order of operations. In $6x+4$ you multiply $6\\cdot x$ before adding 4.",
      "Substituting the value into the wrong place, or into only some of the $x$'s.",
      "Confusing the two directions: substituting to get an output (evaluate) versus solving for the input that produces a given output.",
    ],
    vocabulary: [
      { term: "Input / output", definition: "The value you put into the rule (input, $x$) and the value you get out (output, $y$)." },
      { term: "Substitute", definition: "Replace the variable with a specific value." },
    ],
    quickCheck: {
      prompt: "For the rule $y = 2x + 1$, what do you do to find $y$ when $x = 3$?",
      choices: [
        "Set $2x + 1 = 3$ and solve for $x$",
        "Substitute 3 for $x$: $2(3) + 1 = 7$",
        "Divide 3 by 2",
        "Multiply the whole rule by 3",
      ],
      correctIndex: 1,
      explanation:
        "You are given the input, so substitute it: $2(3)+1 = 7$. Setting the rule equal to a number and solving would be the reverse task - finding the input for a known output.",
    },
  },
  "vertical-line-test": {
    topicId: "vertical-line-test",
    intro:
      "A relation is a function only when every input has exactly one output. On a graph you can check this instantly with the vertical line test: if any vertical line would cross the graph in more than one place, that input has two outputs, so it is not a function. It works because a vertical line is a single x-value, and hitting the graph twice means that one x has two y's.",
    methodSteps: [
      "Picture (or draw) a vertical line and slide it across the whole graph.",
      "If the line ever touches two or more points at the same time, the relation fails - it is not a function.",
      "Working from a list of points instead? Check whether any x-value repeats with a different y-value.",
      "If no x is paired with two different y's, it passes: it is a function.",
    ],
    keyIdea:
      "A vertical line is a single x-value. If it hits the graph in two places, that one input has two outputs - so the relation is not a function. That reasoning matters more than the test's name.",
    pitfalls: [
      "Watching y-values instead of x-values. Repeated y's are fine; only a repeated x with different y's breaks the rule.",
      "Thinking a vertical line is a function - it is not, because that single x has infinitely many y's.",
    ],
    vocabulary: [
      { term: "Function", definition: "A relation where each input is paired with exactly one output." },
      { term: "Relation", definition: "Any set of ordered pairs linking inputs to outputs." },
    ],
    quickCheck: {
      prompt: "Why does a graph fail the vertical line test tell you it is not a function?",
      choices: [
        "Two different x-values share the same y",
        "A single x-value is paired with two different y-values",
        "The graph is not a straight line",
        "The graph crosses the y-axis twice",
      ],
      correctIndex: 1,
      explanation:
        "A vertical line marks one x-value. If it crosses the graph twice, that single input has two outputs, which breaks the definition of a function (each input has exactly one output).",
    },
  },
  "function-notation": {
    topicId: "function-notation",
    intro:
      "Function notation writes the output as $f(x)$ instead of $y$. So $f(3)$ means 'the output of the function when the input is 3'. This is the single most common misread in the topic: $f(3)$ is not $f$ times 3 - the parentheses signal substitution, not multiplication. Once you read it that way, evaluating is just plugging in.",
    methodSteps: [
      "Read $f(a)$ as: substitute $a$ for every $x$ in the rule.",
      "Simplify using order of operations - multiply before you add.",
      "To solve $f(x) = b$ instead, you already know the output; set the rule equal to $b$ and solve for $x$.",
    ],
    keyIdea:
      "$f(x)$ is just a new name for the output $y$. So $f(3)$ means 'the output when the input is 3' - substitute 3 for $x$. It is never $f$ times 3.",
    pitfalls: [
      "Reading $f(3)$ as multiplication. It means evaluate the function at 3.",
      "Substituting into only some of the $x$'s when the rule has more than one.",
    ],
    vocabulary: [
      { term: "$f(x)$", definition: "Function notation for the output of function $f$ at input $x$; it names the output, replacing $y$." },
    ],
    quickCheck: {
      prompt: "What does $f(3)$ mean?",
      choices: [
        "$f$ times 3",
        "The output of the function when the input is 3",
        "$x$ equals 3",
        "The slope is 3",
      ],
      correctIndex: 1,
      explanation:
        "The parentheses signal substitution, not multiplication. $f(3)$ is the output you get when you put 3 in for $x$ - the same value you would have called $y$.",
    },
  },
  zeros: {
    topicId: "zeros",
    intro:
      "A zero of a function is an input that makes the output equal to zero. On a graph, the zeros are the x-intercepts - the spots where the line crosses the x-axis - because every point on the x-axis has a y-value of 0. So finding a zero and finding an x-intercept are the same task, approached from algebra or from a picture.",
    methodSteps: [
      "Set the function rule equal to 0. That captures the meaning of a zero: output equals 0.",
      "Solve the resulting equation for $x$ using inverse operations.",
      "The $x$-value you get is the zero of the function.",
      "On a graph instead, read the $x$-coordinate where the line crosses the x-axis.",
    ],
    keyIdea:
      "A zero is the input where the output is 0, found by solving $f(x)=0$. On a graph it is the x-intercept, because every point on the x-axis has $y=0$. The zero/root is the x-value; the x-intercept is the point $(x, 0)$.",
    pitfalls: [
      "Reporting the y-intercept (where it crosses the y-axis) instead of the x-intercept.",
      "Giving the full point $(x, 0)$ when only the $x$-value is asked for, or vice versa - read the question.",
    ],
    vocabulary: [
      { term: "Zero (root)", definition: "An input value that makes the function's output 0; usually reported as the $x$-value." },
      { term: "x-intercept", definition: "The point where a graph crosses the x-axis, written $(x, 0)$; its y-value is 0." },
    ],
    quickCheck: {
      prompt: "Why are the zeros of a function the same as its x-intercepts?",
      choices: [
        "Because the y-intercept is always 0",
        "Because every point on the x-axis has $y = 0$, and a zero is where the output is 0",
        "Because zeros are always at $x = 0$",
        "Because the slope is 0 there",
      ],
      correctIndex: 1,
      explanation:
        "A zero is an input where the output ($y$) is 0. On the graph, the points with $y=0$ are exactly the points on the x-axis, so the zeros land at the x-intercepts.",
    },
  },

  // --- Unit 4: Graphing Linear Functions --------------------------------
  "graphing-slope-intercept": {
    topicId: "graphing-slope-intercept",
    intro:
      "A line in slope-intercept form is written $y = mx + b$, and those two numbers tell you everything about the graph. The y-intercept $b$ is where the line crosses the y-axis (the point $(0, b)$) - it is your starting point. The slope $m$ is the steepness: rise over run, how far the line goes up (or down, if negative) for each step to the right. Once you plot the intercept and step off the slope, the whole line is determined.",
    keyIdea:
      "In $y = mx + b$: $b$ is the y-intercept (start on the y-axis) and $m$ is the slope (rise over run). Plot the intercept, then use the slope to find a second point.",
    methodSteps: [
      "Read $b$ and plot the y-intercept at $(0, b)$ on the y-axis.",
      "Read $m$ as rise over run. From the intercept, move up/down by the rise and right by the run to plot a second point.",
      "Draw the straight line through the two points.",
      "To go the other way - reading a graph - find where it crosses the y-axis for $b$, then count rise over run between two lattice points for $m$.",
    ],
    pitfalls: [
      "Swapping slope and intercept. In $y = mx + b$, $m$ (next to $x$) is the slope and $b$ (alone) is the y-intercept.",
      "Getting the slope's sign backward. A line going down left-to-right has a negative slope.",
      "Reading the x-intercept when the question asks for the y-intercept (where it crosses the y-axis).",
    ],
    vocabulary: [
      { term: "Slope $m$", definition: "Steepness of a line: rise over run, the change in $y$ per unit change in $x$." },
      { term: "y-intercept $b$", definition: "Where the line crosses the y-axis, the point $(0, b)$." },
      { term: "Slope-intercept form", definition: "The equation form $y = mx + b$, which shows the slope and y-intercept directly." },
    ],
    quickCheck: {
      prompt: "For the line $y = 3x - 2$, what are the slope and y-intercept?",
      choices: [
        "Slope $-2$, y-intercept $3$",
        "Slope $3$, y-intercept $-2$",
        "Slope $3$, y-intercept $2$",
        "Slope $2$, y-intercept $3$",
      ],
      correctIndex: 1,
      explanation:
        "In $y = mx + b$, the coefficient of $x$ is the slope and the constant is the y-intercept. So $y = 3x - 2$ has slope $3$ and y-intercept $-2$.",
    },
  },
};

export function getLesson(topicId: TopicId): Lesson {
  return LESSONS[topicId];
}
